import {
  ColDef,
  ValueFormatterParams,
  ICellRendererParams,
  IServerSideGetRowsParams,
  GridReadyEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import CustomizedBoxTable from "components/Custom/CustomizedBoxTable";
import CustomizedStatus from "components/Custom/CustomizedStatus";
import AgGrid from "components/UI/AgGrid";
import { GraphQLClient } from "graphql-request";
import { useSalesAgGrid } from "hooks/Sales/use-sales-aggrid";
import { RefObject } from "react";
import { createGraphQLClientWithMiddleware } from "services/graphqlClient";
import { DELIVERY_TRIP_INCOME_REPORT } from "services/Logistic/Report/DeliveryTripReportAgGrid";
import { dateFilterModel, dateFilterParams } from "utils/AgGridFilter";
import { formatDate, formatDateTime } from "utils/Date";
import { numberFormatter, statusValueFormatter } from "utils/Formatter/Global";
import { typeValueFormatter } from "utils/Formatter/Logistic/Vehicle";
import { useVehicleTypeValues } from "hooks/Logistic/use-vehicle-type-options";
import { useTemperatureTypeValues } from "hooks/Logistic/use-temperature-type-options";
import { DT_STATUS_ALL } from "types/Logistic/deliveryTripStatus";

interface Props {
  gridRef: RefObject<AgGridReact>;
  height?: string | number;
}

const DeliveryTripIncomeReportTable = ({ gridRef, height }: Props) => {
  const { quotationFilterParams } = useSalesAgGrid("quotation");
  const { fetchValues: fetchVehicleTypeValues } = useVehicleTypeValues();
  const { fetchValues: fetchTemperatureTypeValues } = useTemperatureTypeValues();
  const columnDefs: ColDef[] = [
    {
      field: "unique_id",
      headerName: "เลขที่เอกสาร",
      filter: "agTextColumnFilter",
      minWidth: 200,
    },
    {
      field: "reference_document",
      headerName: "หมายเลขอ้างอิง QT",
      filter: "agSetColumnFilter",
      filterParams: quotationFilterParams,
      valueFormatter: (params: ValueFormatterParams) => {
        if (Array.isArray(params.value) && params.value.length > 0) {
          return params.value.join(", ");
        } else {
          return "-";
        }
      },
      minWidth: 200,
    },
    {
      field: "status",
      headerName: "สถานะ",
      filter: "agSetColumnFilter",
      filterParams: {
        // 86d4889jb — `booking` + `delivering` added. `late` was missing too:
        // overdue trips showed in the report but could not be filtered to.
        values: DT_STATUS_ALL,
        valueFormatter: (params: ValueFormatterParams) =>
          statusValueFormatter(params.value),
      },
      cellRenderer: (params: ICellRendererParams) => (
        <CustomizedStatus status={params.value} />
      ),
      cellStyle: {
        display: "flex",
        justifyContent: "flex-center",
        width: "100%",
      },
      minWidth: 200,
    },
    {
      field: "flag_status",
      headerName: "เกินกำหนด",
      filter: "agSetColumnFilter",
      filterParams: {
        values: [1, 0],
        valueFormatter: ({ value }: { value: number }) => {
          if (value) return "ใช่";
          return "ไม่ใช่";
        },
      },
      cellRenderer: ({ value }: { value: number }) => {
        return <CustomizedStatus status={value ? "late" : ""} />;
      },
      cellStyle: {
        display: "flex",
        justifyContent: "flex-center",
        width: "100%",
      },
      minWidth: 200,
    },
    {
      field: "job_no",
      headerName: "Job Number",
      filter: "agTextColumnFilter",
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value) return params.value || "-";
        else return "-";
      },
      minWidth: 200,
    },
    {
      field: "vehicle_no",
      headerName: "รหัสรถ",
      filter: "agTextColumnFilter",
      minWidth: 200,
    },
    {
      field: "vehicle_license_plate",
      headerName: "ทะเบียนรถ",
      filter: "agTextColumnFilter",
      minWidth: 200,
    },
    {
      field: "trailer_license_plate",
      headerName: "ทะเบียนหาง",
      filter: "agTextColumnFilter",
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value) return params.value || "-";
        else return "-";
      },
      minWidth: 200,
    },
    {
      field: "primary_driver_name",
      headerName: "พนักงานขนส่ง",
      filter: "agTextColumnFilter",
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value) return params.value || "-";
        else return "-";
      },
      minWidth: 200,
    },
    {
      field: "secondary_driver_name",
      headerName: "พนักงานขนส่ง 2",
      filter: "agTextColumnFilter",
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value && params.value !== " ") return params.value || "-";
        else return "-";
      },
      minWidth: 200,
    },
    {
      field: "related_user_list",
      headerName: "ผู้เกี่ยวข้อง",
      filter: false,
      valueFormatter: (params: ValueFormatterParams) => {
        if (Array.isArray(params.value) && params.value.length > 0) {
          return params.value.join(", ");
        } else {
          return "-";
        }
      },
      minWidth: 200,
    },
    {
      field: "created_date",
      headerName: "วันที่สร้าง",
      filter: "agDateColumnFilter",
      filterParams: dateFilterParams,
      valueFormatter: (params: ValueFormatterParams) =>
        formatDate(params.value),
      minWidth: 200,
    },
    {
      field: "issue_date",
      headerName: "วันที่ออกเอกสาร",
      filter: "agDateColumnFilter",
      valueFormatter: (params: ValueFormatterParams) =>
        formatDate(params.value),
      minWidth: 200,
    },
    {
      field: "customer_unique_id",
      headerName: "รหัสลูกค้า",
      filter: "agTextColumnFilter",
      minWidth: 200,
    },
    {
      field: "customer_name",
      headerName: "ชื่อลูกค้า",
      filter: "agTextColumnFilter",
      minWidth: 200,
    },
    {
      field: "vehicle_type",
      headerName: "ประเภทรถ",
      filter: "agSetColumnFilter",
      valueFormatter: (params: ValueFormatterParams) =>
        typeValueFormatter(params.value),
      filterParams: {
        values: async (params: any) => {
          params.success(
            await fetchVehicleTypeValues({ excludeTrailer: true })
          );
        },
        valueFormatter: (params: ValueFormatterParams) =>
          typeValueFormatter(params.value),
      },
      minWidth: 200,
    },
    {
      field: "container_type",
      headerName: "ประเภทตู้",
      filter: "agTextColumnFilter",
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value) return params.value || "-";
        else return "-";
      },
      minWidth: 200,
    },
    {
      field: "item_type",
      headerName: "ประเภทสินค้า",
      filter: "agTextColumnFilter",
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value) return params.value || "-";
        else return "-";
      },
      minWidth: 200,
    },
    {
      field: "item_amount",
      headerName: "มูลค่าสินค้า",
      filter: false,
      valueFormatter: (params) => {
        const value = params.value;
        if (value === null || value === undefined || value === "") {
          return "-";
        }
        return isNaN(value) ? value : numberFormatter(Number(value));
      },
      cellStyle: { display: "flex", justifyContent: "flex-end" },
      headerClass: "ag-end-header",
      minWidth: 200,
    },
    {
      field: "expected_temperature",
      headerName: "อุณหภูมิ",
      filter: false,
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value) return params.value || "-";
        else return "-";
      },
      minWidth: 200,
    },
    {
      field: "temperature_type",
      headerName: "ประเภทอุณหภูมิ",
      filter: "agSetColumnFilter",
      filterParams: {
        values: async (params: any) => {
          params.success(await fetchTemperatureTypeValues());
        },
      },
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value) return params.value || "-";
        else return "-";
      },
      minWidth: 200,
    },
    {
      field: "plan_start_date",
      headerName: "วันที่เวลาเริ่มต้น (แผน)",
      filter: "agDateColumnFilter",
      valueFormatter: (params: ValueFormatterParams) =>
        formatDateTime(params.value),
      minWidth: 200,
    },
    {
      field: "plan_end_date",
      headerName: "วันที่เวลาสิ้นสุด (แผน)",
      filter: "agDateColumnFilter",
      valueFormatter: (params: ValueFormatterParams) =>
        formatDateTime(params.value),
      minWidth: 200,
    },
    {
      field: "actual_start_date",
      headerName: "วันที่เวลาเริ่มต้น (จริง)",
      filter: "agDateColumnFilter",
      valueFormatter: (params: ValueFormatterParams) =>
        params.value ? formatDateTime(params.value) : "-",
      minWidth: 200,
    },
    {
      field: "actual_end_date",
      headerName: "วันที่เวลาสิ้นสุด (จริง)",
      filter: "agDateColumnFilter",
      valueFormatter: (params: ValueFormatterParams) =>
        params.value ? formatDateTime(params.value) : "-",
      minWidth: 200,
    },
    {
      field: "route_code",
      headerName: "Route Code",
      filter: "agTextColumnFilter",
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value) return params.value || "-";
        else return "-";
      },
      minWidth: 200,
    },
    {
      field: "plan_distance",
      headerName: "ระยะทาง (แผน)",
      filter: false,
      valueFormatter: (params: ValueFormatterParams) =>
        params.value != null ? numberFormatter(Number(params.value)) : "-",
      cellStyle: { display: "flex", justifyContent: "flex-end" },
      headerClass: "ag-end-header",
      minWidth: 200,
    },
    {
      field: "fuel_price",
      headerName: "ราคาน้ำมัน",
      filter: false,
      valueFormatter: (params: ValueFormatterParams) =>
        params.value != null ? numberFormatter(Number(params.value)) : "-",
      cellStyle: { display: "flex", justifyContent: "flex-end" },
      headerClass: "ag-end-header",
      minWidth: 200,
    },
    {
      field: "item_id",
      headerName: "รายการ",
      filter: false,
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value) return params.value || "-";
        else return "-";
      },
      minWidth: 200,
    },
    {
      field: "item_unique_id",
      headerName: "รหัสสินค้า (บริการ)",
      filter: "agTextColumnFilter",
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value) return params.value || "-";
        else return "-";
      },
      minWidth: 200,
    },
    {
      field: "item_name",
      headerName: "ชื่อสินค้า (บริการ)",
      filter: "agTextColumnFilter",
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value) return params.value || "-";
        else return "-";
      },
      minWidth: 200,
    },
    {
      field: "item_sku_desc",
      headerName: "รายละเอียดสินค้า (บริการ)",
      filter: "agTextColumnFilter",
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value) return params.value || "-";
        else return "-";
      },
      minWidth: 200,
    },
    {
      field: "qty",
      headerName: "จำนวน",
      filter: false,
      valueFormatter: (params: ValueFormatterParams) =>
        params.value != null ? numberFormatter(Number(params.value)) : "0.00",
      cellStyle: { display: "flex", justifyContent: "flex-end" },
      headerClass: "ag-end-header",
      minWidth: 200,
    },
    {
      field: "uom_name",
      headerName: "หน่วย",
      filter: false,
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value) return params.value || "-";
        else return "-";
      },
      minWidth: 200,
    },
    {
      field: "price_per_unit",
      headerName: "ราคา/หน่วย",
      filter: false,
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value) return numberFormatter(params.value);
        else return "0.00";
      },
      cellStyle: { display: "flex", justifyContent: "flex-end" },
      headerClass: "ag-end-header",
      minWidth: 200,
    },
    // {
    //   field: "discount",
    //   headerName: "ส่วนลด",
    //   filter: false,
    //   valueFormatter: (params: ValueFormatterParams) => {
    //     if (params.value) return numberFormatter(params.value);
    //     else return "0.00";
    //   },
    //   cellStyle: { display: "flex", justifyContent: "flex-end" },
    //   headerClass: "ag-end-header",
    //   minWidth: 200,
    // },
    // {
    //   field: "vat_percentage",
    //   headerName: "ภาษี (%)",
    //   filter: false,
    //   valueFormatter: (params: ValueFormatterParams) => {
    //     if (params.value) return params.value || "-";
    //     else return "-";
    //   },
    //   minWidth: 200,
    // },
    {
      field: "pre_vat_amount",
      headerName: "จำนวนเงิน",
      filter: false,
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value) return numberFormatter(params.value);
        else return "0.00";
      },
      cellStyle: { display: "flex", justifyContent: "flex-end" },
      headerClass: "ag-end-header",
      minWidth: 200,
    },
    // {
    //     field: "withholding_tax_type",
    //     headerName: "ภาษีหัก ณ ที่จ่าย (%)",
    //     filter: false,
    //     valueFormatter: (params: ValueFormatterParams) => {
    //         if (params.value) return (params.value) || "-";
    //         else return "-";
    //     },
    //     minWidth: 200,
    // },
    // {
    //   field: "income_net_amount",
    //   headerName: "มูลค่ารวมสุทธิ",
    //   filter: false,
    //   valueFormatter: (params: ValueFormatterParams) => {
    //     if (params.value) return numberFormatter(params.value);
    //     else return "0.00";
    //   },
    //   cellStyle: { display: "flex", justifyContent: "flex-end" },
    //   headerClass: "ag-end-header",
    //   minWidth: 200,
    // },
    // {
    //     field: "income_total_amount",
    //     headerName: "จำนวนเงินชำระทั้งสิ้น",
    //     filter: false,
    //     valueFormatter: (params: ValueFormatterParams) => {
    //         if (params.value) return numberFormatter(params.value);
    //         else return "0.00";
    //     },
    //     cellStyle: { display: "flex", justifyContent: "flex-end" },
    //     headerClass: "ag-end-header",
    //     minWidth: 200,
    // },
    // หมายเหตุเอกสาร dropped per 86d37db3t — the remark applies to the
    // parent DT, not per income row; it lives on the main DT report.
  ];

  const graphQLClient: GraphQLClient =
    createGraphQLClientWithMiddleware("sales");

  const datasource = {
    async getRows(params: IServerSideGetRowsParams) {
      const { request } = params;
      const { startRow, endRow, filterModel, sortModel } = request;

      const {
        created_date,
        issue_date,
        plan_start_date,
        plan_end_date,
        actual_start_date,
        actual_end_date,
        flag_status,
        reference_document,
      } = filterModel;

      const formatFilter = {
        ...filterModel,
        created_date: dateFilterModel(created_date),
        issue_date: dateFilterModel(issue_date),
        plan_start_date: dateFilterModel(plan_start_date),
        plan_end_date: dateFilterModel(plan_end_date),
        actual_start_date: dateFilterModel(actual_start_date),
        actual_end_date: dateFilterModel(actual_end_date),
        flag_status: flag_status && {
          ...flag_status,
          values: flag_status.values.map((v: string) => parseInt(v)),
        },
        reference_document: reference_document
          ? {
              filterType: "array",
              type: "hasSome",
              values: reference_document.values,
            }
          : undefined,
      };
      const formatSort = [
        ...sortModel,
        { colId: "issue_date", sort: "desc" },
        { colId: "item_id", sort: "asc" },
      ];
      try {
        const { DeliveryTripIncomeReportViewsAggrid } =
          await graphQLClient.request(DELIVERY_TRIP_INCOME_REPORT, {
            aggridInput: {
              startRow,
              endRow,
              filterModel: formatFilter,
              sortModel: formatSort,
            },
          });
        params.success({
          rowData: DeliveryTripIncomeReportViewsAggrid.results,
          rowCount: DeliveryTripIncomeReportViewsAggrid.count,
        });
      } catch (err) {
        console.error("Error loading rows:", err);
        params.fail();
      }
    },
  };

  const onGridReady = (params: GridReadyEvent) => {
    params.api.onFilterChanged();
    params.api.setServerSideDatasource(datasource);
  };

  return (
    <CustomizedBoxTable height={height}>
      <AgGrid
        // height={665}
        ref={gridRef}
        columnDefs={columnDefs}
        onGridReady={onGridReady}
      />
    </CustomizedBoxTable>
  );
};

export default DeliveryTripIncomeReportTable;
